/** Isolated quote-admin IRR price display helpers (English digit storage). */

const FA = "۰۱۲۳۴۵۶۷۸۹";
const AR = "٠١٢٣٤٥٦٧٨٩";

export function toEnglishDigits(input) {
  return String(input ?? "")
    .replace(/[۰-۹]/g, (d) => String(FA.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String(AR.indexOf(d)));
}

/** Keep only digits after normalizing Persian/Arabic numerals. */
export function digitsOnly(input) {
  return toEnglishDigits(input).replace(/\D/g, "");
}

/**
 * Format integer digit-string with English thousands commas.
 * Empty → "".
 */
export function formatIrrGrouped(digitStr) {
  const d = digitsOnly(digitStr);
  if (!d) return "";
  // Avoid leading zeros except single 0
  const normalized = d.replace(/^0+(?=\d)/, "");
  return normalized.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Parse display value → integer or null (empty) or NaN (invalid).
 * Rejects decimals / negatives / non-finite / unsafe integers.
 */
export function parseIrrInput(display) {
  const raw = toEnglishDigits(display).trim();
  if (!raw) return null;
  if (/[.\u066B\u066C٫]/.test(raw)) return NaN;
  const d = digitsOnly(raw);
  if (!d) return NaN;
  if (d.length > 15) return NaN; // beyond safe integer range for money UI
  const n = Number(d);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return NaN;
  if (!Number.isSafeInteger(n)) return NaN;
  return n;
}

export function formatIrrFromNumber(n) {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num) || num < 0 || !Number.isInteger(num)) return "";
  return formatIrrGrouped(String(num));
}

/**
 * Compute new caret index after reformatting with commas.
 * digitCountBeforeCaret = how many digit chars were left of caret in old value.
 */
export function caretAfterFormat(formatted, digitCountBeforeCaret) {
  if (digitCountBeforeCaret <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      seen += 1;
      if (seen === digitCountBeforeCaret) return i + 1;
    }
  }
  return formatted.length;
}

export function countDigitsBefore(str, caret) {
  const left = String(str).slice(0, Math.max(0, caret));
  return digitsOnly(left).length;
}

/**
 * Parse discount percent display → number | null (empty→0 via caller) | NaN invalid.
 * 0–100 inclusive, max 2 decimal places. Persian/Arabic digits OK.
 */
export function parseDiscountPercentInput(display) {
  const raw = toEnglishDigits(display).trim().replace(/%/g, "").trim();
  if (!raw) return null; // blank → treat as 0 by caller
  const cleaned = raw.replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return NaN;
  const parts = cleaned.split(".");
  const whole = parts[0];
  const frac = parts[1] || "";
  if (whole.length > 3) return NaN;
  const scaled = Number(whole) * 100 + Number((frac + "00").slice(0, 2));
  if (!Number.isFinite(scaled) || scaled < 0 || scaled > 10000) return NaN;
  return scaled / 100;
}

export function formatDiscountPercent(n) {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num) || num < 0 || num > 100) return "";
  const scaled = Math.round(num * 100);
  if (scaled % 100 === 0) return String(scaled / 100);
  if (scaled % 10 === 0) return (scaled / 100).toFixed(1);
  return (scaled / 100).toFixed(2);
}
